import { create } from 'zustand';
import { auth, db, storage } from '../firebase';
import {
  onAuthStateChanged,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  updateProfile as updateAuthProfile,
  sendPasswordResetEmail,
  EmailAuthProvider,
  linkWithCredential,
  linkWithPopup,
  linkWithRedirect,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  updatePassword,
  signInAnonymously,
  browserPopupRedirectResolver
} from 'firebase/auth';
import {
  doc,
  getDoc,
  setDoc,
  collection,
  query,
  where,
  getDocs,
  limit,
  orderBy,
  startAt,
  endAt,
  onSnapshot,
  addDoc,
  serverTimestamp
} from 'firebase/firestore';
import { bootstrapService } from '../services/bootstrapService';
import { toast } from 'sonner';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { handleFirestoreError, OperationType } from '../utils/firestoreErrorHandler';
import { useDiagnosticStore } from './useDiagnosticStore';
import { GameExperienceMode, UserProfile } from '../types/UserProfile';
export type { UserProfile };

const SEARCH_USERS_LIMIT = 20;
const SEARCH_USERS_QUERY_LIMIT = 100;
const ALL_SEARCHABLE_USERS_LIMIT = 200;
const SEARCH_ACTIVITY_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

interface AuthState {
  user: UserProfile | null;
  presence: Record<string, { isOnline: boolean; lastSeenAt: string }>;
  loading: boolean;
  loginLoading: boolean;
  isGoogleSigningIn: boolean;
  googleAuthMessage: string | null;
  authInitialized: boolean;
  loginWithGoogle: () => Promise<void>;
  loginWithEmail: (email: string, pass: string) => Promise<void>;
  registerWithEmail: (email: string, pass: string, nickname: string) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  logout: () => Promise<void>;
  initAuth: () => (() => void) | void;
  updateProfile: (nickname: string, avatarUrl: string) => Promise<void>;
  updatePrivacySettings: (settings: Partial<UserProfile['privacy']>) => Promise<void>;
  updateSearchVisibility: (visible: boolean) => Promise<void>;
  updateActivity: () => Promise<void>;
  changePassword: (currentPass: string, newPass: string) => Promise<void>;
  createPasswordForCurrentUser: (newPass: string) => Promise<void>;
  linkGoogleToCurrentUser: () => Promise<void>;
  clearGoogleAuthMessage: () => void;
  updateAdvisor: (enabled: boolean) => Promise<void>;
  updateAdvancedAdvisor: (enabled: boolean) => Promise<void>;
  updateGameExperienceMode: (mode: GameExperienceMode) => Promise<void>;
  uploadAvatar: (file: File) => Promise<string>;
  deleteAccount: () => Promise<void>;
  fetchUserProfile: (uid: string) => Promise<UserProfile | null>;
  searchUsers: (searchTerm: string) => Promise<UserProfile[]>;
  getAllSearchableUsers: () => Promise<UserProfile[]>;
  getRecentOpponents: (uid: string) => Promise<UserProfile[]>;
  userCache: Record<string, { profile: UserProfile; timestamp: number }>;
  getCachedProfile: (uid: string) => UserProfile | null;
  setCachedProfile: (uid: string, profile: UserProfile) => void;
  
  // Presence & Activity
  updatePresence: (isOnline: boolean) => Promise<void>;
  subscribeToPresence: (uid: string) => () => void;
  
  // Blocking & Reporting
  blockUser: (targetUid: string) => Promise<void>;
  unblockUser: (targetUid: string) => Promise<void>;
  reportUser: (report: { reportedUserId: string; chatId?: string; reason: string; comment?: string }) => Promise<void>;
  fetchBlockedUsers: () => Promise<UserProfile[]>;
}

/**
 * Guardas globales para impedir múltiples listeners de auth.
 * Esto evita loops por dobles initAuth() en producción.
 */
let authUnsubscribe: (() => void) | null = null;

const GOOGLE_REDIRECT_PENDING_KEY = 'verbo_google_redirect_pending';
const GOOGLE_LINK_REDIRECT_PENDING_KEY = 'verbo_google_link_redirect_pending';
const GOOGLE_AUTH_CANCEL_MESSAGE = 'No se ha completado el acceso con Google. Puedes intentarlo de nuevo o entrar con email y contraseña.';

const clearGoogleRedirectState = () => {
  sessionStorage.removeItem(GOOGLE_REDIRECT_PENDING_KEY);
  sessionStorage.removeItem(GOOGLE_LINK_REDIRECT_PENDING_KEY);
  localStorage.removeItem('authPending');
  localStorage.removeItem('googleLoginInProgress');
  localStorage.removeItem('redirectPending');
  sessionStorage.removeItem('authPending');
  sessionStorage.removeItem('googleLoginInProgress');
  sessionStorage.removeItem('redirectPending');
};

const isGoogleConflictError = (code?: string) => (
  code === 'auth/account-exists-with-different-credential' ||
  code === 'auth/email-already-in-use' ||
  code === 'auth/credential-already-in-use'
);

const getGoogleProvider = () => {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: 'select_account' });
  return provider;
};

export const isSearchableOpponentProfile = (
  profile: UserProfile,
  currentUid?: string | null
): boolean => {
  if (!profile.uid || profile.uid === currentUid) return false;
  if (profile.opponentSearchVisible === false) return false;
  if (profile.opponentSearchMode === 'hidden') return false;
  if (profile.deletedAt || profile.anonymizedAt || (profile.status as string) === 'deleted') return false;
  return true;
};

const matchesSearchTerm = (profile: UserProfile, term: string): boolean => {
  const normalizedTerm = term.trim().toLowerCase();
  if (!normalizedTerm) return true;
  return [
    profile.nickname_lowercase,
    profile.nickname,
    profile.displayName,
    profile.email,
  ].some((value) => value?.toLowerCase().startsWith(normalizedTerm));
};

const getSearchSortName = (profile: UserProfile): string => (
  profile.nickname_lowercase ||
  profile.nickname ||
  profile.displayName ||
  profile.email ||
  ''
).toLowerCase();

const sortProfilesAlphabetically = (profiles: UserProfile[]) => (
  [...profiles].sort((a, b) => getSearchSortName(a).localeCompare(getSearchSortName(b), 'es'))
);

const filterSearchableProfiles = (
  profiles: UserProfile[],
  currentUid?: string | null
) => sortProfilesAlphabetically(
  profiles.filter((profile) => isSearchableOpponentProfile(profile, currentUid))
);

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  presence: {},
  loading: true,
  loginLoading: false,
  isGoogleSigningIn: false,
  googleAuthMessage: null,
  authInitialized: false,
  userCache: {},

  getCachedProfile: (uid: string) => {
    const cached = get().userCache[uid];
    if (cached && Date.now() - cached.timestamp < 1000 * 60 * 30) {
      return cached.profile;
    }
    return null;
  },

  setCachedProfile: (uid: string, profile: UserProfile) => {
    set(state => ({
      userCache: {
        ...state.userCache,
        [uid]: { profile, timestamp: Date.now() }
      }
    }));
  },

  loginWithGoogle: async () => {
    const diag = useDiagnosticStore.getState();
    const currentState = get();
    const isLocalPreview = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const isEmbedded = window.self !== window.top;
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
    
    diag.addLog('GOOGLE_LOGIN_CLICK', {
      path: window.location.pathname,
      referrer: document.referrer || 'none',
      visibility: document.visibilityState,
      loginLoading: currentState.loginLoading,
      isGoogleSigningIn: currentState.isGoogleSigningIn,
      authInitialized: currentState.authInitialized
    });

    // Guards evaluation
    if (currentState.loginLoading) {
      diag.addLog('GOOGLE_LOGIN_GUARD_BLOCKED', { reason: 'loginLoading is true' });
      return;
    }
    if (currentState.isGoogleSigningIn) {
      diag.addLog('GOOGLE_LOGIN_GUARD_BLOCKED', { reason: 'isGoogleSigningIn is true' });
      return;
    }
    if (!currentState.authInitialized) {
      diag.addLog('GOOGLE_LOGIN_GUARD_BLOCKED', { reason: 'auth not initialized' });
      // We don't return here as we might want to try anyway, or return if strict
    }

    diag.addLog('GOOGLE_LOGIN_START');
    set({ loginLoading: true, isGoogleSigningIn: true, googleAuthMessage: null });

    try {
      const provider = getGoogleProvider();
      
      const useRedirect = isLocalPreview || isEmbedded;
      const method = useRedirect ? 'redirect' : 'popup';
      
      diag.addLog('GOOGLE_LOGIN_MODE_SELECTED', { 
        method, 
        isMobile,
        isStandalone,
        isLocalPreview,
        isEmbedded,
        userAgent: navigator.userAgent 
      });

      if (useRedirect) {
        diag.addLog('GOOGLE_REDIRECT_CALL');
        sessionStorage.setItem(GOOGLE_REDIRECT_PENDING_KEY, '1');
        const { signInWithRedirect: firebaseSignInWithRedirect } = await import('firebase/auth');
        await firebaseSignInWithRedirect(auth, provider, browserPopupRedirectResolver);
        return;
      }

      diag.addLog('GOOGLE_LOGIN_MODE_POPUP');
      diag.addLog('GOOGLE_POPUP_CALL');
      const result = await signInWithPopup(auth, provider, browserPopupRedirectResolver);
      const user = result.user;
      diag.addLog('GOOGLE_LOGIN_POPUP_SUCCESS', { uid: user.uid });
      
      const { user: userData } = await bootstrapService.bootstrap(user.uid);
      
      if (!userData) {
        diag.addLog('LOGIN_GOOGLE_NEW_USER_CREATION');
        const userRef = doc(db, 'users', user.uid);
        const nickname = (user.displayName || 'Jugador').substring(0, 25);
        
        const newUser: UserProfile = {
          uid: user.uid,
          nickname,
          nickname_lowercase: nickname.toLowerCase(),
          displayName: user.displayName || nickname,
          email: user.email || undefined,
          avatarUrl: user.photoURL || '',
          bio: '',
          elo: 1200, league: 'Bronce', status: 'online',
          lastSeen: new Date().toISOString(), createdAt: new Date().toISOString(),
          opponentSearchVisible: true,
          activeUntil: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
          opponentSearchMode: 'public',
          preferredLanguage: 'es', preferredBoard: 'VERBO', preferredBag: 'CLASICA', preferredSpeed: 'NORMAL',
          gameExperienceMode: 'basic',
          allowChat: true, allowChallenges: true, authProvider: 'google',
          privacy: {
            searchableByPlayers: true, allowRandomMatchmaking: true, showOnlineStatus: true,
            showDeliveryConfirmation: true, showReadReceipts: true, allowImages: true, showRealTimeActivity: true,
            whoCanMessageMe: 'everyone'
          },
          friends: [], blockedUsers: [], badges: [], advisorEnabled: false, advancedAdvisorEnabled: true
        };
        await setDoc(userRef, newUser);
        set({ user: newUser, loginLoading: false, isGoogleSigningIn: false });
        diag.addLog('GOOGLE_LOGIN_COMPLETE_NEW_USER');
      } else {
        set({ user: userData, loginLoading: false, isGoogleSigningIn: false });
        diag.addLog('GOOGLE_LOGIN_COMPLETE_EXISTING_USER');
      }
    } catch (error: any) {
      set({ loginLoading: false, isGoogleSigningIn: false });
      diag.addLog('GOOGLE_LOGIN_ERROR', { 
        code: error.code, 
        message: error.message,
        stack: error.stack?.substring(0, 200)
      });
      diag.setLastError(error.code || 'unknown', error.message || 'unknown');

      if (isGoogleConflictError(error.code)) {
        const conflictEmail = error.customData?.email || error.email;
        const suffix = conflictEmail ? ` (${conflictEmail})` : '';
        const message = `Ya existe una cuenta de VerbO con este email${suffix}. Entra con el método que usaste antes y después podrás vincular Google o crear una contraseña desde Seguridad.`;
        set({ googleAuthMessage: message });
        toast.error(message);
        throw error;
      }

      if (error.code !== 'auth/popup-blocked') {
        set({ googleAuthMessage: GOOGLE_AUTH_CANCEL_MESSAGE });
      }
      
      if (error.code === 'auth/popup-blocked') {
        diag.addLog('GOOGLE_POPUP_BLOCKED_REDIRECT_FALLBACK');
        try {
          const provider = getGoogleProvider();
          sessionStorage.setItem(GOOGLE_REDIRECT_PENDING_KEY, '1');
          const { signInWithRedirect: firebaseSignInWithRedirect } = await import('firebase/auth');
          await firebaseSignInWithRedirect(auth, provider, browserPopupRedirectResolver);
          return;
        } catch (redirectError: any) {
          diag.addLog('GOOGLE_REDIRECT_FALLBACK_ERROR', {
            code: redirectError.code,
            message: redirectError.message
          });
          toast.error(`Error Google redirect (${redirectError.code}): ${redirectError.message}`);
          throw redirectError;
        }
      } else if (error.code === 'auth/popup-closed-by-user') {
        toast.info('Inicio de sesión cancelado o ventana cerrada.');
      } else if (error.code === 'auth/cancelled-popup-request') {
        toast.error('Solicitud cancelada por una operación posterior.');
      } else if (error.code === 'auth/unauthorized-domain') {
        const localHint = window.location.hostname === '127.0.0.1'
          ? ' En este preview abre http://localhost:5173/auth en lugar de 127.0.0.1.'
          : '';
        toast.error(`Dominio no autorizado en Firebase Auth.${localHint}`);
      } else if (error.code === 'auth/network-request-failed') {
        toast.error('Error de red: La conexión con Google fue bloqueada. Desactiva bloqueadores de anuncios o abre la app en una pestaña nueva.');
      } else {
        toast.error(`Error Google (${error.code}): ${error.message}`);
      }
      throw error;
    } finally {
      diag.addLog('GOOGLE_LOGIN_FINALLY');
      set((state) => {
        if (state.loginLoading || state.isGoogleSigningIn) {
          return { loginLoading: false, isGoogleSigningIn: false };
        }
        return {};
      });
    }
  },

  loginWithEmail: async (email, pass) => {
    if (get().loginLoading) return;
    set({ loginLoading: true });
    try {
      const result = await signInWithEmailAndPassword(auth, email, pass);
      const user = result.user;
      const { user: userData } = await bootstrapService.bootstrap(user.uid);
      if (userData) {
        set({ user: userData, loginLoading: false });
      } else {
        // If the document doesn't exist, we'll let onAuthStateChanged handle the auto-repair
        // as it runs immediately after signInWithEmailAndPassword success
        console.warn('[Auth] Login successful but user profile missing from Firestore. Waiting for auto-repair...');
      }
    } catch (error: any) {
      set({ loginLoading: false });
      console.error("Email login error:", error);
      if (error.code === 'auth/network-request-failed') {
        toast.error('Error de red. No se pudo contactar con el servidor de autenticación.');
      } else if (error.code === 'auth/wrong-password' || error.code === 'auth/user-not-found') {
        toast.error('Email o contraseña incorrectos.');
      } else {
        toast.error(`Error: ${error.message}`);
      }
      throw error;
    }
  },

  registerWithEmail: async (email, pass, nickname) => {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, pass);
      const user = result.user;
      
      await updateAuthProfile(user, { displayName: nickname });
      
      const userRef = doc(db, 'users', user.uid);
      const newUser: UserProfile = {
        uid: user.uid,
        nickname,
        nickname_lowercase: nickname.toLowerCase(),
        displayName: nickname,
        email: user.email || undefined,
        avatarUrl: '',
        bio: '',
        elo: 1200,
        league: 'Bronce',
        status: 'online',
        lastSeen: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        opponentSearchVisible: true,
        activeUntil: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
        opponentSearchMode: 'public',
        preferredLanguage: 'es',
        preferredBoard: 'VERBO',
        preferredBag: 'CLASICA',
        preferredSpeed: 'NORMAL',
        gameExperienceMode: 'basic',
        allowChat: true,
        allowChallenges: true,
        privacy: {
          searchableByPlayers: true,
          allowRandomMatchmaking: true,
          showOnlineStatus: true,
          showDeliveryConfirmation: true,
          showReadReceipts: true,
          allowImages: true,
          showRealTimeActivity: true,
          whoCanMessageMe: 'everyone'
        },
        friends: [],
        blockedUsers: [],
        badges: [],
        advisorEnabled: false,
        advancedAdvisorEnabled: true
      };
      
      await setDoc(userRef, newUser);
      set({ user: newUser });
    } catch (error) {
      console.error("Email registration error:", error);
      throw error;
    }
  },

  resetPassword: async (email) => {
    const normalizedEmail = email.trim();
    const diag = useDiagnosticStore.getState();
    const resetUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      ? 'https://verboabc.com/auth'
      : `${window.location.origin}/auth`;

    diag.addLog('PASSWORD_RESET_REQUEST', {
      emailDomain: normalizedEmail.split('@')[1] || 'unknown',
      resetUrl
    });

    try {
      await sendPasswordResetEmail(auth, normalizedEmail, {
        url: resetUrl,
        handleCodeInApp: false
      });
      diag.addLog('PASSWORD_RESET_ACCEPTED_BY_FIREBASE', {
        emailDomain: normalizedEmail.split('@')[1] || 'unknown'
      });
    } catch (error: any) {
      console.error("Password reset error:", error);
      diag.addLog('PASSWORD_RESET_ERROR', {
        code: error.code,
        message: error.message
      });
      throw error;
    }
  },

  logout: async () => {
    await signOut(auth);
    bootstrapService.clearCache();
    set({ user: null });
  },

  updateProfile: async (nickname: string, avatarUrl: string) => {
    if (!auth.currentUser) return;
    try {
      const userRef = doc(db, 'users', auth.currentUser.uid);
      const nickname_lowercase = nickname.toLowerCase();
      await setDoc(userRef, { nickname, nickname_lowercase, avatarUrl }, { merge: true });
      set((state) => ({
        user: state.user ? { ...state.user, nickname, nickname_lowercase, avatarUrl } : null
      }));
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `users/${auth.currentUser.uid}`);
    }
  },

  updatePrivacySettings: async (settings) => {
    if (!auth.currentUser) return;
    try {
      const userRef = doc(db, 'users', auth.currentUser.uid);
      await setDoc(userRef, { privacy: settings }, { merge: true });
      set((state) => ({
        user: state.user ? {
          ...state.user,
          privacy: { ...state.user.privacy, ...settings } as UserProfile['privacy']
        } : null
      }));
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `users/${auth.currentUser.uid}`);
    }
  },

  changePassword: async (currentPass: string, newPass: string) => {
    const user = auth.currentUser;
    if (!user || !user.email) throw new Error("No authenticated user");

    try {
      const credential = EmailAuthProvider.credential(user.email, currentPass);
      await reauthenticateWithCredential(user, credential);
      
      await updatePassword(user, newPass);
      
      const userRef = doc(db, 'users', user.uid);
      await setDoc(userRef, { passwordChangedAt: new Date().toISOString() }, { merge: true });
      
      set((state) => ({
        user: state.user ? { ...state.user, passwordChangedAt: new Date().toISOString() } : null
      }));
    } catch (error: any) {
      console.error("Error changing password:", error);
      if (error.code === 'auth/wrong-password') {
        throw new Error("La contraseña actual es incorrecta.");
      }
      throw error;
    }
  },

  createPasswordForCurrentUser: async (newPass: string) => {
    const firebaseUser = auth.currentUser;
    if (!firebaseUser || !firebaseUser.email) {
      throw new Error('No hay un usuario autenticado con email disponible.');
    }

    if (newPass.length < 8) {
      throw new Error('La contraseña debe tener al menos 8 caracteres.');
    }

    const hasPassword = firebaseUser.providerData.some((provider) => provider.providerId === 'password');
    if (hasPassword) {
      throw new Error('Esta cuenta ya tiene una contraseña activa.');
    }

    const linkPassword = async () => {
      const credential = EmailAuthProvider.credential(firebaseUser.email as string, newPass);
      await linkWithCredential(firebaseUser, credential);
      await firebaseUser.reload();
      const userRef = doc(db, 'users', firebaseUser.uid);
      const changedAt = new Date().toISOString();
      await setDoc(userRef, {
        passwordChangedAt: changedAt,
        authProvider: 'email-password'
      }, { merge: true });
      set((state) => ({
        user: state.user ? { ...state.user, passwordChangedAt: changedAt, authProvider: 'email-password' } : null
      }));
    };

    try {
      await linkPassword();
    } catch (error: any) {
      if (error.code === 'auth/requires-recent-login') {
        const hasGoogle = firebaseUser.providerData.some((provider) => provider.providerId === 'google.com');
        if (!hasGoogle) {
          throw new Error('Por seguridad necesitamos confirmar tu identidad antes de crear la contraseña.');
        }
        try {
          await reauthenticateWithPopup(firebaseUser, getGoogleProvider(), browserPopupRedirectResolver);
          await linkPassword();
        } catch (reauthError: any) {
          console.error('Error reauthenticating before password link:', reauthError);
          throw new Error('Por seguridad necesitamos confirmar tu identidad antes de crear la contraseña.');
        }
        return;
      }

      if (isGoogleConflictError(error.code)) {
        throw new Error('Ya existe una cuenta de VerbO con este email. Entra con el método que usaste antes y después podrás vincular Google o crear una contraseña desde Seguridad.');
      }

      throw error;
    }
  },

  linkGoogleToCurrentUser: async () => {
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) throw new Error('No hay un usuario autenticado.');

    const hasGoogle = firebaseUser.providerData.some((provider) => provider.providerId === 'google.com');
    if (hasGoogle) throw new Error('Google ya está vinculado a esta cuenta.');

    const provider = getGoogleProvider();
    const isEmbedded = window.self !== window.top;
    const isLocalPreview = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

    try {
      if (isEmbedded || isLocalPreview) {
        sessionStorage.setItem(GOOGLE_LINK_REDIRECT_PENDING_KEY, '1');
        await linkWithRedirect(firebaseUser, provider, browserPopupRedirectResolver);
        return;
      }

      await linkWithPopup(firebaseUser, provider, browserPopupRedirectResolver);
      await firebaseUser.reload();
      const userRef = doc(db, 'users', firebaseUser.uid);
      await setDoc(userRef, { authProvider: 'google' }, { merge: true });
      set((state) => ({
        user: state.user ? { ...state.user, authProvider: 'google' } : null
      }));
    } catch (error: any) {
      if (error.code === 'auth/popup-blocked') {
        sessionStorage.setItem(GOOGLE_LINK_REDIRECT_PENDING_KEY, '1');
        await linkWithRedirect(firebaseUser, provider, browserPopupRedirectResolver);
        return;
      }

      if (isGoogleConflictError(error.code)) {
        throw new Error('Ya existe una cuenta de VerbO con este email. Entra con el método que usaste antes y después podrás vincular Google o crear una contraseña desde Seguridad.');
      }

      if (error.code === 'auth/popup-closed-by-user' || error.code === 'auth/cancelled-popup-request') {
        throw new Error('No se ha completado la vinculación con Google. Puedes intentarlo de nuevo más tarde.');
      }

      throw error;
    }
  },

  clearGoogleAuthMessage: () => set({ googleAuthMessage: null }),

  updateAdvisor: async (enabled: boolean) => {
    if (!auth.currentUser) return;
    try {
      const userRef = doc(db, 'users', auth.currentUser.uid);
      await setDoc(userRef, { advisorEnabled: enabled }, { merge: true });
      set((state) => ({
        user: state.user ? { ...state.user, advisorEnabled: enabled } : null
      }));
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `users/${auth.currentUser.uid}`);
    }
  },

  updateAdvancedAdvisor: async (enabled: boolean) => {
    if (!auth.currentUser) return;
    try {
      const userRef = doc(db, 'users', auth.currentUser.uid);
      await setDoc(userRef, { advancedAdvisorEnabled: enabled }, { merge: true });
      set((state) => ({
        user: state.user ? { ...state.user, advancedAdvisorEnabled: enabled } : null
      }));
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `users/${auth.currentUser.uid}`);
    }
  },

  updateGameExperienceMode: async (mode: GameExperienceMode) => {
    if (!auth.currentUser) return;
    try {
      const userRef = doc(db, 'users', auth.currentUser.uid);
      await setDoc(userRef, { gameExperienceMode: mode }, { merge: true });
      set((state) => ({
        user: state.user ? { ...state.user, gameExperienceMode: mode } : null
      }));
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `users/${auth.currentUser.uid}`);
    }
  },

  uploadAvatar: async (file: File) => {
    if (!auth.currentUser) throw new Error("No authenticated user");
    const uid = auth.currentUser.uid;
    
    try {
      const base64Promise = new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = (error) => reject(error);
        reader.readAsDataURL(file);
      });

      const base64String = await base64Promise;
      
      const userRef = doc(db, 'users', uid);
      await setDoc(userRef, { avatarUrl: base64String }, { merge: true });
      
      set((state) => ({
        user: state.user ? { ...state.user, avatarUrl: base64String } : null
      }));
      
      return base64String;
    } catch (error: any) {
      console.error(`[avatar] error:`, error);
      throw new Error(`Error al procesar avatar: ${error.message || 'Error desconocido'}`);
    }
  },

  deleteAccount: async () => {
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) return;

    try {
      const userRef = doc(db, 'users', firebaseUser.uid);
      await setDoc(userRef, { status: 'deleted', deletedAt: new Date().toISOString() }, { merge: true });
      
      await firebaseUser.delete();
      
      set({ user: null });
    } catch (error: any) {
      console.error("Error deleting account:", error);
      if (error.code === 'auth/requires-recent-login') {
        throw new Error("Para eliminar tu cuenta, debes haber iniciado sesión recientemente. Por favor, cierra sesión e inicia sesión de nuevo antes de intentarlo.");
      }
      throw error;
    }
  },

  initAuth: () => {
    const diag = useDiagnosticStore.getState();
    diag.addLog('AUTH_INIT_START');

    if (get().authInitialized) {
      diag.addLog('AUTH_INIT_ALREADY_DONE');
      return authUnsubscribe || undefined;
    }

    set({ authInitialized: true });

    // Handle redirect result for mobile once
    const handleRedirectResult = async () => {
      diag.addLog('REDIRECT_RESULT_START');
      diag.setRedirectStatus('START');
      const hadLoginRedirect = sessionStorage.getItem(GOOGLE_REDIRECT_PENDING_KEY) === '1';
      const hadLinkRedirect = sessionStorage.getItem(GOOGLE_LINK_REDIRECT_PENDING_KEY) === '1';
      
      // Defensive timeout: if getRedirectResult hangs, don't block the app
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('TIMEOUT_REDIRECT')), 5000)
      );

      try {
        const { getRedirectResult: firebaseGetRedirectResult } = await import('firebase/auth');
        diag.addLog('FIREBASE_AUTH_MODULE_LOADED');

        const result = await Promise.race([
          firebaseGetRedirectResult(auth, browserPopupRedirectResolver),
          timeoutPromise
        ]) as any;

        if (result?.user) {
          diag.addLog('REDIRECT_RESULT_OK', { uid: result.user.uid });
          diag.setRedirectStatus('OK');
          clearGoogleRedirectState();
          set({ loginLoading: true, isGoogleSigningIn: true });
          
          try {
            const { user: userData } = await bootstrapService.bootstrap(result.user.uid);
            if (userData) {
              if (hadLinkRedirect) {
                const userRef = doc(db, 'users', result.user.uid);
                await setDoc(userRef, { authProvider: 'google' }, { merge: true });
                userData.authProvider = 'google';
                toast.success('Google vinculado. Ahora puedes entrar con Google o con email y contraseña.');
              }
              set({ user: userData, loginLoading: false, isGoogleSigningIn: false });
            } else {
              diag.addLog('REDIRECT_NEW_USER_BOOTSTRAP');
              const user = result.user;
              const userRef = doc(db, 'users', user.uid);
              const nickname = (user.displayName || 'Jugador').substring(0, 25);
              const newUser: UserProfile = {
                uid: user.uid,
                nickname,
                nickname_lowercase: nickname.toLowerCase(),
                displayName: user.displayName || nickname,
                email: user.email || undefined,
                avatarUrl: user.photoURL || '',
                bio: '',
                elo: 1200, league: 'Bronce', status: 'online',
                lastSeen: new Date().toISOString(), createdAt: new Date().toISOString(),
                opponentSearchVisible: true,
                activeUntil: new Date(Date.now() + SEARCH_ACTIVITY_WINDOW_MS).toISOString(),
                opponentSearchMode: 'public',
                preferredLanguage: 'es', preferredBoard: 'VERBO', preferredBag: 'CLASICA', preferredSpeed: 'NORMAL',
                gameExperienceMode: 'basic',
                allowChat: true, allowChallenges: true, authProvider: 'google',
                privacy: {
                  searchableByPlayers: true, allowRandomMatchmaking: true, showOnlineStatus: true,
                  showDeliveryConfirmation: true, showReadReceipts: true, allowImages: true, showRealTimeActivity: true,
                  whoCanMessageMe: 'everyone'
                },
                friends: [], blockedUsers: [], badges: [], advisorEnabled: false, advancedAdvisorEnabled: true
              };
              await setDoc(userRef, newUser);
              set({ user: newUser, loginLoading: false, isGoogleSigningIn: false });
            }
          } catch (bootError: any) {
            diag.addLog('REDIRECT_BOOTSTRAP_ERROR', { message: bootError.message });
            set({ loginLoading: false, isGoogleSigningIn: false });
          }
        } else {
          diag.addLog('REDIRECT_RESULT_NULL');
          diag.setRedirectStatus('NULL');
          if (hadLoginRedirect || hadLinkRedirect) {
            clearGoogleRedirectState();
            set({ googleAuthMessage: GOOGLE_AUTH_CANCEL_MESSAGE });
            toast.info(GOOGLE_AUTH_CANCEL_MESSAGE);
          }
        }
      } catch (error: any) {
        clearGoogleRedirectState();
        if (error.message === 'TIMEOUT_REDIRECT') {
          diag.addLog('REDIRECT_RESULT_TIMEOUT');
          diag.setRedirectStatus('TIMEOUT');
          if (hadLoginRedirect || hadLinkRedirect) {
            set({ googleAuthMessage: GOOGLE_AUTH_CANCEL_MESSAGE });
            toast.info(GOOGLE_AUTH_CANCEL_MESSAGE);
          }
        } else {
          console.error('[Auth] Redirect error details:', {
            code: error.code,
            message: error.message,
            stack: error.stack
          });
          diag.addLog('REDIRECT_RESULT_ERROR', { code: error.code, message: error.message });
          diag.setRedirectStatus('ERROR');
          diag.setLastError(error.code || 'unknown', error.message || 'unknown');
          const message = isGoogleConflictError(error.code)
            ? 'Ya existe una cuenta de VerbO con este email. Entra con el método que usaste antes y después podrás vincular Google o crear una contraseña desde Seguridad.'
            : GOOGLE_AUTH_CANCEL_MESSAGE;
          set({ googleAuthMessage: message });
          toast.error(message);
        }
        set({ isGoogleSigningIn: false, loginLoading: false });
      } finally {
        clearGoogleRedirectState();
        set({ isGoogleSigningIn: false, loginLoading: false });
      }
    };

    handleRedirectResult();

    diag.addLog('AUTH_READY_FOR_STATE_CHANGE');

    authUnsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      diag.addLog('ON_AUTH_STATE_CHANGED', { hasUser: !!firebaseUser, uid: firebaseUser?.uid });
      try {
        const currentUser = get().user;

        if (firebaseUser && currentUser?.uid === firebaseUser.uid) {
          diag.addLog('AUTH_STATE_REDUNDANT_SKIP');
          set({ loading: false });
          return;
        }

        if (firebaseUser) {
          diag.addLog('AUTH_STATE_BOOTSTRAP_START', { uid: firebaseUser.uid });
          const { user: userData } = await bootstrapService.bootstrap(firebaseUser.uid);

          if (userData) {
            if (!userData.email && firebaseUser.email) {
              const userRef = doc(db, 'users', firebaseUser.uid);
              await setDoc(userRef, { email: firebaseUser.email }, { merge: true });
              userData.email = firebaseUser.email;
            }

            set({ user: userData, loading: false });
            diag.addLog('AUTH_STATE_READY', { nickname: userData.nickname });
          } else {
            diag.addLog('AUTH_STATE_ORPHANED_AUTO_REPAIR_START');
            const nickname = (firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'Jugador').substring(0, 25);
            const newUser: UserProfile = {
              uid: firebaseUser.uid,
              nickname,
              nickname_lowercase: nickname.toLowerCase(),
              displayName: firebaseUser.displayName || nickname,
              email: firebaseUser.email || undefined,
              avatarUrl: firebaseUser.photoURL || '',
              bio: '',
              elo: 1200, league: 'Bronce', status: 'online',
              lastSeen: new Date().toISOString(), createdAt: new Date().toISOString(),
              opponentSearchVisible: true,
              activeUntil: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
              opponentSearchMode: 'public',
              preferredLanguage: 'es', preferredBoard: 'VERBO', preferredBag: 'CLASICA', preferredSpeed: 'NORMAL',
              gameExperienceMode: 'basic',
              allowChat: true, allowChallenges: true, authProvider: firebaseUser.providerData[0]?.providerId === 'google.com' ? 'google' : 'email-password',
              privacy: {
                searchableByPlayers: true, allowRandomMatchmaking: true, showOnlineStatus: true,
                showDeliveryConfirmation: true, showReadReceipts: true, allowImages: true, showRealTimeActivity: true,
                whoCanMessageMe: 'everyone'
              },
              friends: [], blockedUsers: [], badges: [], advisorEnabled: false, advancedAdvisorEnabled: true
            };
            await setDoc(doc(db, 'users', firebaseUser.uid), newUser);
            set({ user: newUser, loading: false });
            diag.addLog('AUTH_STATE_ORPHANED_AUTO_REPAIR_COMPLETE');
          }
        } else {
          bootstrapService.clearCache();
          set({ user: null, loading: false });
          diag.addLog('AUTH_STATE_NO_USER');
        }
      } catch (error: any) {
        console.error('[Auth] Error in onAuthStateChanged:', error);
        diag.addLog('AUTH_STATE_ERROR', { message: error.message });
        set({ loading: false });
      }
    });

    // Safety timeout: If auth takes more than 10s, force loading to false
    setTimeout(() => {
      if (get().loading) {
        console.warn('[Auth] Safety timeout reached! Forcing loading to false.');
        set({ loading: false });
      }
    }, 10000);

    return authUnsubscribe;
  },

  fetchUserProfile: async (uid: string) => {
    if (!uid || !auth.currentUser) return null;
    
    const cached = get().getCachedProfile(uid);
    if (cached) return cached;

    if (uid === 'ROBOT') {
      const robotProfile: UserProfile = {
        uid: 'ROBOT',
        nickname: '...',
        nickname_lowercase: '...',
        displayName: '...',
        avatarUrl: 'https://api.dicebear.com/7.x/bottts/svg?seed=robot',
        bio: '...',
        elo: 1500,
        league: 'Oro',
        status: 'online',
        lastSeen: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        preferredLanguage: 'es',
        preferredBoard: 'classic',
        preferredBag: 'standard',
        preferredSpeed: 'normal',
        allowChat: false,
        allowChallenges: true,
        friends: [],
        blockedUsers: [],
        badges: [],
        advisorEnabled: false,
        advancedAdvisorEnabled: false
      };
      get().setCachedProfile('ROBOT', robotProfile);
      return robotProfile;
    }
    try {
      const userRef = doc(db, 'users', uid);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        const profile = userSnap.data() as UserProfile;
        get().setCachedProfile(uid, profile);
        return profile;
      }
      return null;
    } catch (error) {
      handleFirestoreError(error, OperationType.GET, `users/${uid}`);
      return null;
    }
  },

  searchUsers: async (searchTerm: string) => {
    if (!searchTerm.trim() || !auth.currentUser) return [];
    const currentUid = auth.currentUser.uid;
    try {
      const usersRef = collection(db, 'users');
      const term = searchTerm.toLowerCase();
      const q = query(
        usersRef,
        orderBy('nickname_lowercase'),
        startAt(term),
        endAt(term + '\uf8ff'),
        limit(SEARCH_USERS_QUERY_LIMIT)
      );
      const querySnapshot = await getDocs(q);
      const profiles = querySnapshot.docs.map(doc => doc.data() as UserProfile);
      return filterSearchableProfiles(profiles, currentUid)
        .slice(0, SEARCH_USERS_LIMIT);
    } catch (error) {
      console.warn("[Auth] Search failed, falling back to broad user scan:", error);
      // Fallback for legacy users who might not have the fields indexed or existing
      try {
        const usersRef = collection(db, 'users');
        const term = searchTerm.toLowerCase();
        const q = query(usersRef, limit(ALL_SEARCHABLE_USERS_LIMIT));
        const querySnapshot = await getDocs(q);
        const profiles = querySnapshot.docs
          .map(doc => doc.data() as UserProfile)
          .filter(profile => matchesSearchTerm(profile, term));
        return filterSearchableProfiles(profiles, currentUid)
          .slice(0, SEARCH_USERS_LIMIT);
      } catch (innerError) {
        handleFirestoreError(innerError, OperationType.LIST, 'users');
        return [];
      }
    }
  },

  getAllSearchableUsers: async () => {
    if (!auth.currentUser) return [];
    const currentUid = auth.currentUser.uid;
    try {
      const usersRef = collection(db, 'users');
      const q = query(usersRef, limit(ALL_SEARCHABLE_USERS_LIMIT));
      const querySnapshot = await getDocs(q);
      const profiles = querySnapshot.docs
        .map(doc => {
          const profile = doc.data() as UserProfile;
          get().setCachedProfile(profile.uid, profile);
          return profile;
        });
      return filterSearchableProfiles(profiles, currentUid);
    } catch (error) {
      handleFirestoreError(error, OperationType.LIST, 'users');
      return [];
    }
  },

  updateSearchVisibility: async (visible: boolean) => {
    if (!auth.currentUser) return;
    try {
      const userRef = doc(db, 'users', auth.currentUser.uid);
      await setDoc(userRef, { 
        opponentSearchVisible: visible,
        updatedAt: serverTimestamp()
      }, { merge: true });
      set(state => ({
        user: state.user ? { ...state.user, opponentSearchVisible: visible } : null
      }));
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `users/${auth.currentUser.uid}`);
    }
  },

  updateActivity: async () => {
    if (!auth.currentUser) return;
    try {
      const userRef = doc(db, 'users', auth.currentUser.uid);
      const now = new Date();
      
      // activeUntil es el corazón del filtrado para evitar oponentes inactivos.
      // Se establece a 7 días en el futuro cada vez que el usuario realiza una acción válida.
      const activeUntil = new Date();
      activeUntil.setDate(now.getDate() + 7);
      
      const isoNow = now.toISOString();
      const isoActiveUntil = activeUntil.toISOString();

      await setDoc(userRef, { 
        lastMoveAt: isoNow,
        activeUntil: isoActiveUntil, // Filtro crítico: solo jugadores con activeUntil >= now aparecen en búsquedas
        updatedAt: serverTimestamp()
      }, { merge: true });
      
      set(state => ({
        user: state.user ? { 
          ...state.user, 
          lastMoveAt: isoNow,
          activeUntil: isoActiveUntil
        } : null
      }));
    } catch (error) {
      console.error("[Auth] Failed to update activity:", error);
    }
  },

  getRecentOpponents: async (uid: string) => {
    if (!uid || !auth.currentUser) {
      console.warn('getRecentOpponents called but uid or auth.currentUser is null');
      return [];
    }

    try {
      const gamesRef = collection(db, 'games');
      const q = query(
        gamesRef,
        where('players', 'array-contains', uid),
        orderBy('createdAt', 'desc'),
        limit(50)
      );
      const querySnapshot = await getDocs(q);
      const opponentIds = new Set<string>();
      querySnapshot.docs.forEach(doc => {
        const data = doc.data();
        const oppId = data.players.find((id: string) => id !== uid);
        if (oppId && oppId !== 'ROBOT') {
          opponentIds.add(oppId);
        }
      });

      const opponents: UserProfile[] = [];
      const ids = Array.from(opponentIds).slice(0, 20);
      
      for (let i = 0; i < ids.length; i += 10) {
        const chunk = ids.slice(i, i + 10);
        const userPromises = chunk.map(async (oppId) => {
          const cached = get().getCachedProfile(oppId);
          if (cached) return cached;

          const userRef = doc(db, 'users', oppId);
          try {
            const userSnap = await getDoc(userRef);
            if (userSnap.exists()) {
              const profile = userSnap.data() as UserProfile;
              get().setCachedProfile(oppId, profile);
              return profile;
            }
          } catch (error) {
            handleFirestoreError(error, OperationType.GET, `users/${oppId}`);
          }
          return null;
        });
        const results = await Promise.all(userPromises);
        opponents.push(...results.filter((u): u is UserProfile => u !== null));
      }
      
      return opponents;
    } catch (error) {
      handleFirestoreError(error, OperationType.LIST, 'games');
      return [];
    }
  },

  updatePresence: async (isOnline: boolean) => {
    if (!auth.currentUser) return;
    try {
      const presenceRef = doc(db, 'presence', auth.currentUser.uid);
      await setDoc(presenceRef, {
        userId: auth.currentUser.uid,
        isOnline,
        lastSeenAt: serverTimestamp()
      }, { merge: true });
      
      const userRef = doc(db, 'users', auth.currentUser.uid);
      await setDoc(userRef, {
        status: isOnline ? 'online' : 'offline',
        lastSeen: new Date().toISOString()
      }, { merge: true });
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `presence/${auth.currentUser.uid}`);
    }
  },

  subscribeToPresence: (uid: string) => {
    const presenceRef = doc(db, 'presence', uid);
    return onSnapshot(presenceRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        set((state) => ({
          presence: { ...state.presence, [uid]: { isOnline: data.isOnline, lastSeenAt: data.lastSeenAt } }
        }));
      }
    });
  },

  blockUser: async (targetUid: string) => {
    if (!auth.currentUser) return;
    try {
      const blockId = `${auth.currentUser.uid}_${targetUid}`;
      const blockRef = doc(db, 'blockedUsers', blockId);
      await setDoc(blockRef, {
        blockerId: auth.currentUser.uid,
        blockedId: targetUid,
        createdAt: serverTimestamp()
      });
      
      const userRef = doc(db, 'users', auth.currentUser.uid);
      const currentBlocked = get().user?.blockedUsers || [];
      if (!currentBlocked.includes(targetUid)) {
        const newBlocked = [...currentBlocked, targetUid];
        await setDoc(userRef, { blockedUsers: newBlocked }, { merge: true });
        set(state => ({
          user: state.user ? { ...state.user, blockedUsers: newBlocked } : null
        }));
      }
    } catch (error) {
      handleFirestoreError(error, OperationType.CREATE, `blockedUsers/${auth.currentUser.uid}_${targetUid}`);
    }
  },

  unblockUser: async (targetUid: string) => {
    if (!auth.currentUser) return;
    try {
      const blockId = `${auth.currentUser.uid}_${targetUid}`;
      const blockRef = doc(db, 'blockedUsers', blockId);
      const { deleteDoc } = await import('firebase/firestore');
      await deleteDoc(blockRef);
      
      const userRef = doc(db, 'users', auth.currentUser.uid);
      const currentBlocked = get().user?.blockedUsers || [];
      if (currentBlocked.includes(targetUid)) {
        const newBlocked = currentBlocked.filter(id => id !== targetUid);
        await setDoc(userRef, { blockedUsers: newBlocked }, { merge: true });
        set(state => ({
          user: state.user ? { ...state.user, blockedUsers: newBlocked } : null
        }));
      }
    } catch (error) {
      handleFirestoreError(error, OperationType.DELETE, `blockedUsers/${auth.currentUser.uid}_${targetUid}`);
    }
  },

  reportUser: async (report) => {
    if (!auth.currentUser) return;
    try {
      const reportsRef = collection(db, 'user_reports');
      await addDoc(reportsRef, {
        ...report,
        reporterId: auth.currentUser.uid,
        timestamp: serverTimestamp()
      });
    } catch (error) {
      handleFirestoreError(error, OperationType.CREATE, 'user_reports');
    }
  },

  fetchBlockedUsers: async () => {
    if (!auth.currentUser) return [];
    try {
      const blockedIds = get().user?.blockedUsers || [];
      if (blockedIds.length === 0) return [];
      
      const users: UserProfile[] = [];
      for (const uid of blockedIds) {
        const user = await get().fetchUserProfile(uid);
        if (user) users.push(user);
      }
      return users;
    } catch (error) {
      console.error("Error fetching blocked users:", error);
      return [];
    }
  }
}));
